import React, { useEffect, useRef, useState } from "react";
import {
  formatCorrectionDelta,
  formatSignedDelta,
} from "./correctionStorage";

export const CorrectionAmountCell = ({
  base,
  extra,
  money = false,
  prefix = null,
  digits = 2,
}) => {
  const total = (Number(base) || 0) + (Number(extra) || 0);
  const changed = (Number(extra) || 0) !== 0;
  const deltaLabel = formatSignedDelta(extra, { money });
  const display = money
    ? Math.round(total).toLocaleString()
    : parseFloat(total).toFixed(digits);
  return (
    <td
      className={changed ? "correction-sum-changed" : ""}
      style={{ textAlign: "center" }}
    >
      {prefix}
      {display}
      {changed && deltaLabel ? (
        <span
          className={`correction-sum-delta${
            Number(extra) < 0 ? " is-negative" : ""
          }`}
        >
          {deltaLabel}
        </span>
      ) : null}
    </td>
  );
};

export const CorrectionEditableCell = ({ value, disabled, onCommit }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [localValue, setLocalValue] = useState(String(value ?? 0));
  const inputRef = useRef(null);
  const originalRef = useRef(value);

  useEffect(() => {
    if (!editing) {
      setLocalValue(String(value ?? 0));
    }
  }, [value, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const commit = async () => {
    const parsed = parseFloat(String(localValue).replace(",", "."));
    if (localValue === "" || Number.isNaN(parsed)) {
      setLocalValue(String(originalRef.current ?? 0));
      setEditing(false);
      return;
    }
    if (parsed === Number(originalRef.current)) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onCommit(parsed);
      setEditing(false);
    } catch (err) {
      setLocalValue(String(originalRef.current ?? 0));
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (disabled) {
    return (
      <span className="correction-value-locked">
        {formatCorrectionDelta(value)}
      </span>
    );
  }

  if (editing || saving) {
    return (
      <input
        ref={inputRef}
        className="correction-effort-input"
        type="text"
        inputMode="decimal"
        value={localValue}
        disabled={saving}
        onChange={(e) => setLocalValue(e.target.value.replace(/,/g, "."))}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") {
            setLocalValue(String(originalRef.current ?? 0));
            setEditing(false);
          }
        }}
      />
    );
  }

  return (
    <span
      className="correction-value-editable"
      onClick={() => {
        originalRef.current = value;
        setLocalValue(String(value ?? 0));
        setEditing(true);
      }}
    >
      {formatCorrectionDelta(value)}
    </span>
  );
};
